// @flow
import React, { useMemo } from 'react';
import styled from 'styled-components';
import Lozenge from '@atlaskit/lozenge';
import Tooltip from '@atlaskit/tooltip';
import { ObjectResult } from '@atlaskit/quick-search';
import Select from '@atlaskit/select';
import ArrowRightCircleIcon from '@atlaskit/icon/glyph/arrow-right-circle';
import { token } from '@atlaskit/tokens';
import { ISSUE_STATUS_CATEGORY_IDS } from '../../utils/consts';
import { openIssuesUrl } from '../../services/jira-services';

const Wrapper = styled.div`
    padding-top: 10px;
    width: 100%;
`;

const SelectWrapper = styled.div`
    > * {
        display: inline-block;
    }

    #days {
        width: 220px;

        > div {
            background-color: ${token('elevation.surface.overlay')};
            border-width: 1px;
        }
    }
`;

const NumbersWrapper = styled.div`
    margin-top: 14px;
    cursor: default;

    > div {
        border-bottom: 1px solid var(--ds-border);

        > div {
            display: inline-block;
            padding-bottom: 6px;

            > .count {
                font-size: 32px;
                line-height: 1em;
                display: inline-block;
                vertical-align: top;
                margin-right: 7px;
            }

            > .label {
                display: inline-block;
                vertical-align: top;
                line-height: 1.25em;
            }

            + div {
                margin-left: 16px;
            }

            &#done {
                border-bottom: 3px solid #048759;
                .count {
                    color: #048759;
                }
            }

            &#in-progress {
                border-bottom: 3px solid #0252cc;
                .count {
                    color: #0252cc;
                }
            }

            &#not-started {
                border-bottom: 3px solid #6b778c;
                .count {
                    color: #6b778c;
                }
            }
        }
    }
`;

const ListWrapper = styled.div`
    margin-top: 14px;

    > div > a.view-in-search {
        > * {
            display: inline-block;
            vertical-align: middle;
            cursor: pointer;

            :hover {
                text-decoration: underline;
            }
        }

        > span {
            height: 18px;
        }
    }

    > div > div.list {
        margin-top: 7px;
    }

    > div > div.list a {
        background-color: ${token('elevation.surface.overlay')};
    }
`;

const IssueKeyText = styled.span`
    text-decoration: ${({ isDone }) => (isDone ? 'line-through' : 'none')};
    color: rgb(137, 147, 164);
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;
    padding-right: 8px;
`;

const PriorityIcon = styled.img`
    height: 20px;
    margin-right: 10px;
    display: inline-block;
    vertical-align: middle;
`;

const ObjectResultAfter = ({ status, priority }) => {
    const { name, id } = status.statusCategory;

    let appearance = 'default';
    if (id === ISSUE_STATUS_CATEGORY_IDS.IN_PROGRESS) appearance = 'inprogress';
    else if (id === ISSUE_STATUS_CATEGORY_IDS.DONE) appearance = 'success';

    return (
        <div>
            {priority && priority.iconUrl && <PriorityIcon src={priority.iconUrl} />}
            <div style={{ verticalAlign: 'middle', display: 'inline-block' }}>
                <Lozenge appearance={appearance}>{name}</Lozenge>
            </div>
        </div>
    );
};

function IssuesList({ onPeriodChange, periodValue, periodOptions, issues, issuesJql, type }) {
    const issuesByStatusCategoryId = useMemo(() => {
        return (issues ?? []).reduce((acc, curr) => {
            const statusCategoryId = curr?.fields?.status?.statusCategory?.id;

            return {
                ...acc,
                [statusCategoryId]: [...(acc[statusCategoryId] ?? []), curr],
            };
        }, {});
    }, [issues]);

    const groupedIssues = useMemo(() => {
        return Object.values(ISSUE_STATUS_CATEGORY_IDS).flatMap(
            (statusCategoryId) => issuesByStatusCategoryId[statusCategoryId] ?? [],
        );
    }, [issuesByStatusCategoryId]);

    return (
        <Wrapper>
            {issues && (
                <>
                    {type && type === 'release' ? null : (
                        <SelectWrapper>
                            <Select
                                id="days"
                                value={periodValue}
                                options={periodOptions}
                                onChange={onPeriodChange}
                                isSearchable={false}
                            />
                        </SelectWrapper>
                    )}
                    <NumbersWrapper>
                        <div>
                            <div id="done">
                                <div className="count">
                                    {issuesByStatusCategoryId[ISSUE_STATUS_CATEGORY_IDS.DONE]?.length || 0}
                                </div>
                                <div className="label">
                                    Issues
                                    <br />
                                    done
                                </div>
                            </div>
                            <div id="in-progress">
                                <div className="count">
                                    {issuesByStatusCategoryId[ISSUE_STATUS_CATEGORY_IDS.IN_PROGRESS]?.length || 0}
                                </div>
                                <div className="label">
                                    Issues
                                    <br />
                                    in progress
                                </div>
                            </div>
                            <div id="not-started">
                                <div className="count">
                                    {issuesByStatusCategoryId[ISSUE_STATUS_CATEGORY_IDS.TO_DO]?.length || 0}
                                </div>
                                <div className="label">
                                    Issues
                                    <br />
                                    to do
                                </div>
                            </div>
                        </div>
                    </NumbersWrapper>

                    <ListWrapper>
                        <div>
                            <a onClick={() => openIssuesUrl(issuesJql)} className="view-in-search">
                                <p>VIEW IN SEARCH</p>
                                <ArrowRightCircleIcon />
                            </a>
                            <div className="list">
                                {groupedIssues.map((issue) => (
                                    <Tooltip key={issue.key} content={issue.fields.summary}>
                                        <ObjectResult
                                            resultId={issue.key}
                                            name={
                                                <div>
                                                    <IssueKeyText
                                                        isDone={
                                                            issue.fields.status.statusCategory.id ===
                                                            ISSUE_STATUS_CATEGORY_IDS.DONE
                                                        }
                                                    >
                                                        {issue.key}
                                                    </IssueKeyText>{' '}
                                                    {issue.fields.summary}
                                                </div>
                                            }
                                            avatarUrl={issue.fields.issuetype && issue.fields.issuetype.iconUrl}
                                            elemAfter={
                                                <ObjectResultAfter
                                                    status={issue.fields.status}
                                                    priority={issue.fields.priority}
                                                />
                                            }
                                            onClick={() => window.PlatformAdapter.openIssueDialog(issue.key)}
                                        />
                                    </Tooltip>
                                ))}
                            </div>
                        </div>
                    </ListWrapper>
                </>
            )}
        </Wrapper>
    );
}

export default IssuesList;
